import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Headers from "./Headers";
import Footer from "./Footer";
import api from "../api/api";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useSelector } from "react-redux";
import ProductDetailsModal from "./ProductDetailsModal";

const pageNameMapping = {
  "about-our-shop": "About our Shop",
  "blogs": "Blogs",
  "delivery-information": "Delivery Information",
  "privacy-policy": "Privacy Policy",
  "terms-and-conditions": "Terms and Conditions",
  "shipping-policy": "Shipping Policy",
  "return-policy": "Return Policy"
};

const pageIcons = {
  "about-our-shop": "🏪",
  "blogs": "📝",
  "delivery-information": "🚚",
  "privacy-policy": "🔒",
  "terms-and-conditions": "📜",
  "shipping-policy": "📦",
  "return-policy": "↩️"
};

const StaticPage = ({ sellerId, sellerName: propSellerName }) => {
  const { sellerName: paramSellerName, pageName } = useParams();
  const { seller, supportInfo } = useSelector((state) => state.auth);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Use prop sellerName if available, otherwise use param
  const finalSellerName = propSellerName || paramSellerName;
  
  // Get sellerId from localStorage/sessionStorage as fallback
  const storedSellerId = localStorage.getItem('sellerId') || sessionStorage.getItem('sellerId');

  const handleBannerClick = (banner) => {
    // If banner has product information, show product modal
    if (banner.product) {
      setSelectedProduct(banner.product);
      setIsModalOpen(true);
    }
    // If banner has productId but no populated product, try to navigate by slug
    else if (banner.productSlug) {
      window.location.href = `/${finalSellerName}/product/details/${banner.productSlug}`;
    }
    // If link is a product slug, navigate to product page
    else if (banner.link) {
      window.location.href = `/${finalSellerName}/product/details/${banner.link}`;
    }
  };
  

  


  // Set document title
  useEffect(() => {
    document.title = "4POS eCommerce Customer";
  }, []);

  const responsiveBanner = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        if (!seller?._id && !finalSellerName) {
          return;
        }
        setLoading(true);
        
        // Try to use seller ID if available, otherwise use seller name
        const sellerIdentifier = seller?._id || storedSellerId || finalSellerName;
        const { data } = await api.get(`/links-content/${sellerIdentifier}`);
        
        if (data?.linksContent) {
          const mappedPageName = pageNameMapping[pageName] || pageName;
          
          // Map the page name to the corresponding field in the response
          const contentKey = {
            'About our Shop': 'aboutShop',
            'Delivery Information': 'deliveryInfo',
            'Privacy Policy': 'privacyPolicy',
            'Terms and Conditions': 'termsAndConditions',
            'Shipping Policy': 'shippingPolicy',
            'Return Policy': 'returnPolicy',
            'Blogs': 'blogs'
          }[mappedPageName];
          
          // Check if content exists for this page
          if (contentKey && data.linksContent[contentKey] && data.linksContent[contentKey].trim() !== '') {
            setContent(data.linksContent[contentKey]);
          } else {
            setContent("Content not found");
          }
        } else {
          setContent("Content not found");
        }
        
        // Fetch banners for the header
        try {
          const bannerResponse = await api.get(`/banners/${sellerIdentifier}`);
          setBanners(bannerResponse.data || []);
        } catch (bannerError) {
          setBanners([]);
        }
      } catch (error) {
        setContent("Content not found");
      } finally {
        setLoading(false);
      }
    };

    if ((seller?._id || storedSellerId || finalSellerName) && pageName) {
      fetchContent();
    }
  }, [seller?._id, storedSellerId, finalSellerName, pageName]);

  // Show loading state while waiting for seller data
  if (!seller?._id) {
    return (
      <div className="flex flex-col min-h-screen">
        <Headers sellerId={finalSellerName} />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden min-h-[70vh] flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                <p className="text-gray-600">Loading seller information...</p>
              </div>
            </div>
          </div>
        </main>
        <Footer sellerId={finalSellerName} />
      </div>
    );
  }

  const mappedPageName = pageNameMapping[pageName] || pageName;
  const pageIcon = pageIcons[pageName] || "📄";

  return (
    <div className="flex flex-col min-h-screen">
      <Headers sellerId={finalSellerName} />
      
      {/* Hero Section with Banner */}
      <section className="h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left">
        <Carousel
          autoPlay={true}
          infinite={true}
          arrows={false}
          showDots={false}
          responsive={responsiveBanner}
          className="h-full"
        >
          {banners && banners.length > 0 ? (
            banners.map((banner, index) => (
              <div 
                key={index} 
                className="h-full w-full cursor-pointer group"
                onClick={() => handleBannerClick(banner)}
              >
                <img
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  src={banner.banner}
                  alt=""
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))
          ) : (
            <div className="h-full w-full bg-gradient-to-r from-indigo-600 to-purple-600" />
          )}
        </Carousel>
        <div className="absolute left-0 top-0 w-full h-full bg-black/40 backdrop-blur-[2px]">
          <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="flex flex-col justify-center gap-2 items-center h-full w-full text-white">
              <div className="text-4xl mb-2">{pageIcon}</div>
              <h2 className="text-3xl font-semibold">{mappedPageName}</h2>
              <div className="flex justify-center items-center gap-2 text-sm">
                <Link to={`/${finalSellerName}`} className="hover:text-indigo-300 transition-colors duration-300">Home</Link>
                <span className="pt-1">
                  <MdOutlineKeyboardArrowRight />
                </span>
                <span>{mappedPageName}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden min-h-[70vh] flex flex-col">
            {loading ? (
              <div className="flex-grow flex items-center justify-center p-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                  <p className="text-gray-600">Loading content...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="border-b border-gray-100 p-6 bg-gradient-to-r from-indigo-50 to-purple-50">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{pageIcon}</span>
                    <h6 className="text-2xl font-semibold text-gray-800">
                      {mappedPageName}
                    </h6>
                  </div>
                </div>
                <div className="flex-grow p-8">
                  <div className="prose prose-lg max-w-3xl mx-auto">
                    {content === "Content not found" ? (
                      <div className="text-center py-8">
                        <p className="text-gray-600 mb-4">No content has been added for this page yet.</p>
                        <p className="text-sm text-gray-500">Please check back later or contact the seller for more information.</p>
                      </div>
                    ) : (
                      content.split("\n").map((paragraph, index) => (
                        <p
                          key={index}
                          className="mb-6 text-gray-600 leading-relaxed text-base"
                        >
                          {paragraph}
                        </p>
                      ))
                    )}
                  </div>
                </div>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 mt-auto border-t border-gray-100">
                  <div className="max-w-3xl mx-auto text-center">
                    <p className="text-gray-600 mb-2">
                      Thank you for visiting our {mappedPageName.toLowerCase()} page.
                    </p>
                    <Link 
                      to={`/${finalSellerName}`}
                      className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 transition-colors duration-300"
                    >
                      <span>Return to Home</span>
                      <MdOutlineKeyboardArrowRight />
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer sellerId={finalSellerName} />

      {/* Product Details Modal */}
      <ProductDetailsModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
        sellerName={finalSellerName}
        sellerId={sellerId || storedSellerId}
        supportInfo={supportInfo}
      />
    </div>
  );
};

export default StaticPage;
